// components/letter_format_customizer.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowDownTrayIcon,
  BookOpenIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  XMarkIcon,
  CloudArrowUpIcon,
  SparklesIcon,
  PaintBrushIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import ManIcon from '@mui/icons-material/Man';
import Woman2Icon from '@mui/icons-material/Woman2';

import {
  uploadLetterCustomTemplates,
  downloadExampleTemplate,
  downloadExistingTemplate,
  getLetterCustomTemplateStatus,
} from "../services/api_lettere";

function getTemplateFileName(letter, sesso = "M") {
  const path =
    sesso === "F"
      ? letter?.template_lettera_donna
      : letter?.template_lettera_uomo || letter?.template_lettera;

  return path?.split("/")?.pop() || "template.docx";
}

function downloadBlob(response, fallbackName = "template.docx") {
  const blobUrl = URL.createObjectURL(response.data);
  const anchor = document.createElement("a");

  anchor.href = blobUrl;
  anchor.download = fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(blobUrl);
}

export default function LetterFormatCustomizerBanner({
  token,
  letter,
  filePresenti = { uomo: false, donna: false },
  onUploaded,
  editorBaseUrl = "/lettere/template-editor",
}) {
  const [open, setOpen] = useState(false);
  const [currentFilePresenti, setCurrentFilePresenti] = useState(filePresenti);
  const [templateUomo, setTemplateUomo] = useState(null);
  const [templateDonna, setTemplateDonna] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [error, setError] = useState("");

  const placeholders = useMemo(
    () => (Array.isArray(letter?.fields) ? letter.fields : []),
    [letter]
  );

  const hasCustomTemplate = Boolean(
    currentFilePresenti?.uomo || currentFilePresenti?.donna
  );
  
  useEffect(() => {
    if (!token || !letter?.id) return;

    let cancelled = false;

    const loadStatus = async () => {
      try {
        setLoadingStatus(true);
        const data = await getLetterCustomTemplateStatus(token, letter.id);

        if (cancelled) return;

        setCurrentFilePresenti(
          data?.file_presenti || { uomo: false, donna: false }
        );
      } catch (err) {
        console.error("Errore caricamento stato template:", err);
        if (!cancelled) {
          setCurrentFilePresenti({ uomo: false, donna: false });
        }
      } finally {
        if (!cancelled) setLoadingStatus(false);
      }
    };

    loadStatus();

    return () => {
      cancelled = true;
    };
  }, [token, letter?.id]);

  const validateFile = (file) => {
    if (!file) return true;
    return file.name.toLowerCase().endsWith(".docx");
  };

  const handleDownloadExample = async () => {
    if (!token || !letter?.id) return;

    try {
      setError("");
      setDownloading("example");

      const response = await downloadExampleTemplate(token, letter.id);
      downloadBlob(response, getTemplateFileName(letter, "M"));
    } catch (err) {
      console.error("Errore download esempio:", err);
      setError("Impossibile scaricare il template di esempio.");
    } finally {
      setDownloading("");
    }
  };

  const handleDownloadExisting = async (sesso) => {
    if (!token || !letter?.id) return;

    try {
      setError("");
      setDownloading(sesso);

      const response = await downloadExistingTemplate(token, letter.id, sesso);
      downloadBlob(response, getTemplateFileName(letter, sesso));
    } catch (err) {
      console.error("Errore download template caricato:", err);
      setError("Impossibile scaricare il template personalizzato.");
    } finally {
      setDownloading("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!templateUomo && !templateDonna) {
      setError("Carica almeno un Template Uomo o un Template Donna.");
      return;
    }

    if (!validateFile(templateUomo) || !validateFile(templateDonna)) {
      setError("I file devono essere in formato .docx.");
      return;
    }

    const formData = new FormData();

    if (templateUomo) formData.append("templateUomo", templateUomo);
    if (templateDonna) formData.append("templateDonna", templateDonna);

    try {
      setUploading(true);

      const data = await uploadLetterCustomTemplates(token, letter.id, formData);

      setCurrentFilePresenti(
        data?.file_presenti || { uomo: false, donna: false }
      );

      setTemplateUomo(null);
      setTemplateDonna(null);
      setOpen(false);

      onUploaded?.(data);
    } catch (err) {
      console.error("Errore upload template:", err);
      setError(
        err?.response?.data?.message ||
          "Errore nel caricamento del template."
      );
    } finally {
      setUploading(false);
    }
  };

  const getLiveEditorUrl = (sesso) => {
    return `${editorBaseUrl}/${letter.id}/${sesso}`;
  };

  if (!letter) return null;

  return (
    <div className="mb-4 overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-sm">
      <div className="bg-gradient-to-r from-indigo-50 via-white to-indigo-50 p-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-indigo-100 p-2 text-indigo-600">
              {hasCustomTemplate ? (
                <PaintBrushIcon className="h-6 w-6" />
              ) : (
                <SparklesIcon className="h-6 w-6" />
              )}
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-semibold text-gray-900">
                  {hasCustomTemplate
                    ? "Format personalizzato attivo"
                    : "Stai usando il format standard"}
                </h2>

                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    hasCustomTemplate
                      ? "bg-green-100 text-green-700"
                      : "bg-indigo-100 text-indigo-700"
                  }`}
                >
                  {loadingStatus
                    ? "Verifica..."
                    : hasCustomTemplate
                    ? "Personalizzato"
                    : "Default"}
                </span>
              </div>

              <p className="mt-1 text-sm text-gray-600">
                {hasCustomTemplate ? (
                  `La lettera “${letter.nome}” verrà generata utilizzando il template personalizzato della tua azienda.`
                ) : (
                  <>
                    La lettera “{letter.nome}” verrà generata utilizzando il template predefinito.
                    <br />
                    Puoi personalizzarlo selezionando <strong>"Personalizza"</strong>, quindi scaricando il template in formato .docx tramite <strong>"Download Template"</strong> e ricaricandolo, oppure modificandolo direttamente dalla piattaforma con la funzione <strong>"Modifica live"</strong>.
                  </>
                )}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
          >
            <DocumentTextIcon className="h-4 w-4" />
            {hasCustomTemplate ? "Gestisci" : "Personalizza"}
            {open ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {open ? (
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 border-t border-indigo-100 px-4 py-4">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleDownloadExample}
                disabled={downloading === "example"}
                className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                {downloading === "example"
                  ? "Download..."
                  : "Download esempio"}
              </button>

              <button
                type="button"
                onClick={() => setShowInstructions(true)}
                className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100"
              >
                <BookOpenIcon className="h-4 w-4" />
                Istruzioni
              </button>
            </div>

            {(currentFilePresenti?.uomo || currentFilePresenti?.donna) ? (
              <div className="grid gap-3 md:grid-cols-2">
                {currentFilePresenti?.uomo ? (
                  <ExistingTemplateAlert
                    type="M"
                    label="Template Uomo"
                    loading={downloading === "M"}
                    onDownload={() => handleDownloadExisting("M")}
                    editorUrl={getLiveEditorUrl("M")}
                  />
                ) : null}

                {currentFilePresenti?.donna ? (
                  <ExistingTemplateAlert
                    type="F"
                    label="Template Donna"
                    loading={downloading === "F"}
                    onDownload={() => handleDownloadExisting("F")}
                    editorUrl={getLiveEditorUrl("F")}
                  />
                ) : null}
              </div>
            ) : null}

            {error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <FileInput
                label="Carica Template Uomo"
                file={templateUomo}
                onChange={setTemplateUomo}
                disabled={uploading}
                editorUrl={!currentFilePresenti?.uomo ? getLiveEditorUrl("M") : null}
              />

              <FileInput
                label="Carica Template Donna"
                file={templateDonna}
                onChange={setTemplateDonna}
                disabled={uploading}
                editorUrl={!currentFilePresenti?.donna ? getLiveEditorUrl("F") : null}
              />
            </div>
          </div>

          <div className="flex justify-end border-t border-gray-100 bg-gray-50 px-4 py-3">
            <button
              type="submit"
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60"
            >
              <CloudArrowUpIcon className="h-4 w-4" />
              {uploading ? "Caricamento..." : "Salva template"}
            </button>
          </div>
        </form>
      ) : null}

      {showInstructions ? (
        <InstructionsModal
          letter={letter}
          placeholders={placeholders}
          onClose={() => setShowInstructions(false)}
        />
      ) : null}
    </div>
  );
}

function ExistingTemplateAlert({ type, label, loading, onDownload, editorUrl }) {
  const Icon = type === "F" ? Woman2Icon : ManIcon;

  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-3 text-sm text-indigo-700">
      <div className="flex min-w-0 items-center gap-2">
        <Icon className="h-6 w-6 shrink-0" />

        <div className="min-w-0 text-xs">
          <div className="truncate">
            <strong>{label}</strong> già caricato.
          </div>

          <span className="block truncate">
            Per sostituirlo carica un nuovo file.
          </span>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <a
          href={editorUrl}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-green-400 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100"
        >
          <DocumentTextIcon className="h-4 w-4" />
          Modifica live
        </a>

        <button
          type="button"
          onClick={onDownload}
          disabled={loading}
          className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          {loading ? "Download..." : "Scarica"}
        </button>
      </div>
    </div>
  );
}

function FileInput({ label, file, onChange, disabled, editorUrl }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="block text-sm font-semibold text-gray-800">
          {label}
        </span>

        {editorUrl ? (
          <a
            href={editorUrl}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-green-400 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100"
          >
            <DocumentTextIcon className="h-4 w-4" />
            Modifica live
          </a>
        ) : null}
      </div>
      <div className="mb-3">  
        <p className="text-xs text-gray-600">
          I file selezionati devono essere in formato{" "}
          <strong>.docx</strong>
        </p>
      </div>

      <input
        type="file"
        accept=".docx"
        disabled={disabled}
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        className="block w-full text-sm text-indigo-600 file:mr-4 file:rounded-lg file:border-0 file:bg-indigo-200 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100 hover:file:border-0 bg-indigo-50/50 rounded-lg " 
      />

      {/* {file ? (
        <p className="mt-2 truncate text-xs text-gray-500">
          File selezionato: {file.name}
        </p>
      ) : null} */}
    </div>
  );
}

function InstructionsModal({ letter, placeholders, onClose }) {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 px-4"
      style={{ zIndex: 999 }}
    >
      <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-white px-5 py-4">
          <h3 className="text-sm font-bold text-indigo-700 uppercase">
            <BookOpenIcon className="h-4 w-4 inline-block me-2" />
            Istruzioni personalizzazione
          </h3>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-auto px-5 py-4 text-sm text-gray-700">
          <p className="mb-3">
            Per personalizzare il template della lettera{" "}
            <strong>{letter?.nome}</strong>:
          </p>

          <ol className="mb-5 list-decimal space-y-2 pl-5">
            <li>Scarica il template di esempio.</li>
            <li>Modifica il documento Word mantenendo i placeholder necessari.</li>
            <li>Carica la versione uomo e/o donna.</li>
            <li>Salva il template.</li>
          </ol>

          <h4 className="mb-2 text-sm font-bold text-gray-900">
            Placeholder disponibili
          </h4>

          <div className="divide-y divide-gray-100 rounded-xl border border-gray-200">
            {placeholders.map((placeholder) => (
              <div
                key={placeholder.id}
                className="flex flex-col gap-1 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <span className="font-medium text-gray-800">
                  {placeholder.nome}
                </span>

                <code className="rounded bg-indigo-50 px-2 py-1 text-xs text-indigo-700">
                  {"{{ "}
                  {placeholder.placeholder}
                  {" }}"}
                </code>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end border-t border-gray-200 bg-gray-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
